import { BrandKey, BrandThemeConfig } from './types';
import { getBrandConfig } from './registry';
import {
  initActiveBrand,
  setActiveBrandKey,
} from './resolveBrand';
import { changeFavicon } from '../../utils/changeFavicon';
import { HALE_FAVICON_URL } from './hale.config';
import { getBrandDisplayName } from '../../hooks/useBrandDisplayName';

const BRAND_STYLESHEET_ID = 'brand-theme-stylesheet';

const STUDENT_FAVICON = {
  href: `${process.env.PUBLIC_URL || ''}/favicons/sparkl.svg`,
  type: 'image/svg+xml',
};

const applyBrandFavicon = (brandKey: BrandKey): void => {
  if (brandKey === 'hale') {
    changeFavicon(HALE_FAVICON_URL);
    return;
  }

  document
    .querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]',
    )
    .forEach((link) => {
      link.href = STUDENT_FAVICON.href;
      link.type = STUDENT_FAVICON.type;
    });
};

const clearInlineBrandVariables = (config: BrandThemeConfig): void => {
  const root = document.documentElement;
  Object.keys(config.cssVariables).forEach((name) => {
    root.style.removeProperty(name);
  });
};

const applyCssVariables = (config: BrandThemeConfig): void => {
  const root = document.documentElement;

  Object.entries(config.cssVariables).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });

  root.style.setProperty(
    '--brand-font-family',
    config.cssVariables['--brand-font-family'],
  );
  document.body.style.fontFamily =
    config.cssVariables['--brand-font-family'];
};

const loadBrandStylesheet = (brandKey: BrandKey): void => {
  let link = document.getElementById(
    BRAND_STYLESHEET_ID,
  ) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement('link');
    link.id = BRAND_STYLESHEET_ID;
    link.rel = 'stylesheet';
    link.type = 'text/css';
    document.head.appendChild(link);
  }

  link.href = `${process.env.PUBLIC_URL || ''}/themes/${brandKey}.css`;
};

export const applyBrandTheme = (brandKey: BrandKey): BrandThemeConfig => {
  setActiveBrandKey(brandKey);
  const config = getBrandConfig(brandKey);
  const root = document.documentElement;

  // Remove previous brand inline vars so student keeps original Sparkl styling
  clearInlineBrandVariables(getBrandConfig('hale'));
  clearInlineBrandVariables(getBrandConfig('student'));

  root.setAttribute('data-brand', config.key);
  document.title = getBrandDisplayName(brandKey);
  applyBrandFavicon(brandKey);

  if (brandKey === 'student') {
    document.body.style.removeProperty('font-family');
    loadBrandStylesheet(brandKey);
    return config;
  }

  applyCssVariables(config);
  loadBrandStylesheet(brandKey);

  return config;
};

/**
 * Initializes brand theme on app load: resolves brand, applies CSS variables,
 * and loads the brand-specific stylesheet.
 */
export const initBrandTheme = (): BrandThemeConfig => {
  const brandKey = initActiveBrand();
  return applyBrandTheme(brandKey);
};

export const switchBrandTheme = (brandKey: BrandKey): BrandThemeConfig =>
  applyBrandTheme(brandKey);
